import { JSONSchema4 } from "json-schema";
import * as zip from "@zip.js/zip.js";
import anime from 'animejs';
import highlighter from 'highlight.js';

import { createPopper, Instance as PopperInstance } from '@popperjs/core';

// @ts-ignore
import scrollIntoView from 'scroll-into-view';

import { JsonSchemaDocumentationGenerator } from "@subgraph-io/edge/json-schema/JsonSchemaDocumentationGenerator.js";
import { GetDefaultsOptions } from "@subgraph-io/edge/json-schema/JsonSchemaProcessor.js";

import { DOM, DOMEvent } from "@subgraph-io/edge-ui/dom/DOM.js";

import type { JADEDocumentationViewerOptions, DocResourceKind, DocResourceKindMapping, DeploymentEnvironment } from "@sixclear/server/src/shared-types/jade/types";

import type { SessionStandalone, SessionStandaloneEventMap } from "@sixclear/static-site-client/src/session_standalone";
import type { ComponentClassEventPayload } from "@subgraph-io/edge/component/Component";

import { stringifyVersion } from '@sixclear/server/src/shared-functions/jade/versioning.js';

import 'normalize.css';
import '../../node_modules/highlight.js/styles/stackoverflow-light.css';
import '../../assets/css/Font-Anonymous-Pro.css';
import '../../assets/css/Font-Montserrat.css';
import '../../assets/fontawesome-pro-6.4.2-web/css/all.min.css';

import "./styles.css";


type JADEConfigurationEditorNotificationType = 'error' | 'warning' | 'note';

type JADEConfigurationEditor = {
    render: () => void,
    destroy: () => void,
    addNotification: (message: string, notificationType: JADEConfigurationEditorNotificationType) => void
    getMonacoInstance: () => any
};

export class JADEDocumentationBrowserClient {

    protected session: SessionStandalone;
    protected deploymentEnvironment: DeploymentEnvironment;
    protected docKind: keyof DocResourceKindMapping | 'error';
    protected docResource: DocResourceKindMapping[keyof DocResourceKindMapping] | undefined;
    protected jadeExampleDownloadUrl: string;

    protected root: DOM;
    protected gridDOM: HTMLElement;
    protected resizeHandleDOM: HTMLElement;
    protected resizeTOCHandleDOM: HTMLElement;
    protected docDOM: HTMLDivElement;
    protected docGridDOM: HTMLDivElement;
    protected navContentDOM: HTMLDivElement;
    protected docTypeMetadataDOM: HTMLDivElement;
    protected docTitleDOM: HTMLDivElement;
    protected userControlsDOM: HTMLDivElement;
    protected docContentDOM: HTMLDivElement;
    protected docTOCDOM: HTMLDivElement;
    protected docTOCOpenPanelDOM: HTMLDivElement;
    protected docTOCClosePanelDOM: HTMLDivElement;
    protected navOpenButtonDOM: HTMLDivElement;
    protected navCloseButtonDOM: HTMLDivElement;
    protected popoverOverlay: HTMLDivElement;

    protected jadeSelectPopper: PopperInstance | undefined;
    protected desktopLoggedInMenuPopper: PopperInstance | undefined;

    protected schemaDocGen: JsonSchemaDocumentationGenerator | undefined;
    protected currentPluginJSONSchema?: JSONSchema4;

    protected editor?: JADEConfigurationEditor;

    protected navColumnWidth: number;
    protected inCollapsibleNavMode: null | boolean;
    protected inCollapsibleTOCMode: null | boolean;
    protected docTOCColumnWidth: number;
    protected docTOCColumnWidthOnMouseDown: number;
    protected clientXOnMouseDown: number;
    protected navColumnWidthOnMouseDown: number;

    protected minContentWidth: number;

    protected isDownloadingExample: boolean;

    constructor(session: SessionStandalone, options: JADEDocumentationViewerOptions, jadeExampleDownloadUrl: string) {
        this.session = session;
        this.deploymentEnvironment = options.deploymentEnvironment !== undefined ? options.deploymentEnvironment : 'online';
        this.docKind = options.kind;

        if (options.kind !== 'error') {
            this.docResource = options.resource;

            // store a doc gen instance, since we'll likely use it when we navigate to a plugin doc
            this.schemaDocGen = new JsonSchemaDocumentationGenerator({}, { id: "schemaDocs" });
        }

        this.jadeExampleDownloadUrl = jadeExampleDownloadUrl;
        this.inCollapsibleNavMode = null;
        this.inCollapsibleTOCMode = null;
        this.isDownloadingExample = false;
        this.navColumnWidth = window.innerWidth < 800 ? window.innerWidth - 10 : 350;
        this.docTOCColumnWidth = window.innerWidth < 800 ? window.innerWidth - 10 : 350;
        this.minContentWidth = 400;

        this.editor = undefined;

        // initialize values used in panel size calculations
        this.clientXOnMouseDown = 0;
        this.navColumnWidthOnMouseDown = 0;
        this.docTOCColumnWidthOnMouseDown = 0;

        // configure code highlighter
        highlighter.configure({
            languages: ['json', 'jsonc']
        });

        // create a root DOM instance on the body
        this.root = new DOM(document.getElementsByTagName('body')[0]);

        // store DOM references
        this.gridDOM = document.getElementById('main') as HTMLDivElement;
        this.docDOM = document.getElementById('doc') as HTMLDivElement;
        this.navContentDOM = document.getElementById('nav-content') as HTMLDivElement;
        this.navOpenButtonDOM = document.getElementById('nav-open') as HTMLDivElement;
        this.navCloseButtonDOM = document.getElementById('nav-close') as HTMLDivElement;
        this.docGridDOM = document.getElementById('doc-grid') as HTMLDivElement;
        this.docTypeMetadataDOM = document.getElementById('doc-type-metadata') as HTMLDivElement;
        this.docTitleDOM = document.getElementById('doc-title') as HTMLDivElement;
        this.userControlsDOM = document.getElementById('user-controls') as HTMLDivElement;
        this.docContentDOM = document.getElementById('doc-content') as HTMLDivElement;
        this.docTOCOpenPanelDOM = document.getElementById('open-toc') as HTMLDivElement;
        this.docTOCClosePanelDOM = document.getElementById('close-toc') as HTMLDivElement;
        this.docTOCDOM = document.getElementById('doc-toc') as HTMLDivElement;
        this.resizeHandleDOM = document.getElementById('handle') as HTMLDivElement;
        this.resizeTOCHandleDOM = document.getElementById('doc-toc-handle') as HTMLDivElement;

        // bind close menu functions so we can remove them as needed to share the popoverOverlay
        this.closeLoggedInMenu = this.closeLoggedInMenu.bind(this);
        this.closeJadeVersionSelect = this.closeJadeVersionSelect.bind(this);

        // create click overlay for handling dropdown close
        this.popoverOverlay = document.createElement('div');
        this.popoverOverlay.style.display = 'none';
        this.popoverOverlay.style.background = 'rgba(0, 0, 0, 0)';
        this.popoverOverlay.style.zIndex = '9998';
        this.popoverOverlay.style.position = 'absolute';
        this.popoverOverlay.style.top = '0';
        this.popoverOverlay.style.right = '0';
        this.popoverOverlay.style.bottom = '0';
        this.popoverOverlay.style.left = '0';
        DOM.append(this.popoverOverlay, this.gridDOM);

        // react to history api events so we get back/forward button behavior
        window.onpopstate = this.onPopState.bind(this);

        // bind user pointer events
        this.root.registerForEvent('#nav-open', 'click', this.onNavOpenSelected, this);
        this.root.registerForEvent('#nav-close', 'click', this.onNavCloseSelected, this);
        this.root.registerForEvent('#handle', 'mousedown', this.onNavResizeMouseDown, this);
        this.root.registerForEvent('#doc-toc-handle', 'mousedown', this.onTOCResizeMouseDown, this);
        this.root.registerForEvent('#open-toc', 'click', this.onTOCOpenSelected, this);
        this.root.registerForEvent('#close-toc', 'click', this.onTOCCloseSelected, this);
        this.root.registerForEvent('.header-anchor', 'click', this.onPermalinkSelected, this);
        this.root.registerForEvent('.toc a', 'click', this.onTOCLinkSelected, this);
        this.root.registerForEvent('.nav-tree-leaf', 'click', this.onDocSelected, this);
        this.root.registerForEvent('.nav-tree-parent', 'click', this.onNavTreeParentSelected, this);

        // bind jade version dropdown
        this.root.registerForEvent('.jade-version-select', 'click', this.onJadeVersionSelectClicked, this);

        // bind example download example button
        this.root.registerForEvent('#download-example', 'click', this.onDownloadExampleSelected, this);

        // bind window events
        window.addEventListener('resize', this.onWindowResize.bind(this));
        window.addEventListener('load', this.onDocLoad.bind(this));
        window.addEventListener('load', this.onWindowResize.bind(this));

        // bind user menu button click
        this.root.registerForEvent('#logged-in-icon-btn', 'click', this.onUserMenuButtonClicked, this);
        this.root.registerForEvent('#desktop-logout', 'click', this.onLogout, this);

        // make sure to trigger a render on editor when orientation changes
        const portraitMode = window.matchMedia("(orientation: portrait)");
        portraitMode.addEventListener("change", (event: MediaQueryListEvent) => {
            if (this.editor) {
                this.editor.render();
            }
        });

        this.session.registerForEvent('CLASS_EVENT', 'authStateChanged', this.onAuthStateChanged, this);

    }

    public async navigateToDocument(url: string, kind: keyof DocResourceKindMapping) {

        // show loader

        // select nav leaf and deselect all others
        let selectedEl: HTMLElement | undefined;
        const navLeafAnchorEls = this.navContentDOM.querySelectorAll('a');

        navLeafAnchorEls.forEach((el: HTMLAnchorElement) => {
            if (new URL(el.href).pathname === new URL(url).pathname) {
                el.classList.add('selected');
                selectedEl = el;
            }
            else {
                el.classList.remove('selected');
            }
        });

        if (selectedEl) {
            this.openAllNavParents(selectedEl);
        }

        // TODO ensure leaf is in view, if it isn't scroll to bring it into view

        // kill the editor instance if we had one in content
        if (this.editor !== undefined) {
            this.editor.destroy();
            this.editor = undefined;
        }

        // fetch content and toc markup and the content data
        // DOCS-FORK
        const urlPath = new URL(url).pathname;
        const [contentHTML] = await Promise.all([
            this.fetchContentHTML(urlPath)
        ]);

        // update bookkeeping properties
        // this.docResource = contentJSON;
        this.docKind = kind;

        // update doc type metadata styles
        this.docTypeMetadataDOM.classList.remove(...this.docTypeMetadataDOM.classList);
        this.docTypeMetadataDOM.classList.add(kind);

        // resolve the title for page using json
        const title = 'Title' // resolvePageTitleForDocPath(contentJSON)

        // replace title
        this.docTitleDOM.innerText = title;

        // update window chrome title
        this.updateWindowTitle(title);

        // replace content
        this.docContentDOM.innerHTML = contentHTML;

        // TODO generate TOC from content fetched
        const contentTOCHTML = `A TOC`;

        // replace toc
        this.docTOCDOM.classList.remove(...this.docTOCDOM.classList);
        this.docTOCDOM.classList.add(kind);
        this.docTOCDOM.innerHTML = `
            <h4><i class="fa-solid fa-link"></i> On this Page</h4>
            ${contentTOCHTML}
        `;

        // update styles on toc open panel button
        this.docTOCOpenPanelDOM.classList.remove(...this.docTOCOpenPanelDOM.classList);
        this.docTOCOpenPanelDOM.classList.add(kind);

        // hide loader

        // trigger on load logic
        this.onDocLoad();
    }

    public setViewerToCollapsibleNavMode() {
        if (this.inCollapsibleNavMode !== true) {
            this.inCollapsibleNavMode = true;
            this.setNavColumnWidth(0);
            this.resizeHandleDOM.style.cursor = 'inherit';
            this.navOpenButtonDOM.style.display = 'flex';
            this.navCloseButtonDOM.style.display = 'none';
        }
    }

    public setViewerToUncollapsedNavMode() {
        if (this.inCollapsibleNavMode !== false) {
            this.inCollapsibleNavMode = false;
            this.setNavColumnWidth(350);
            this.resizeHandleDOM.style.cursor = 'ew-resize';
            this.navOpenButtonDOM.style.display = 'none';
            this.navCloseButtonDOM.style.display = 'none';
        }
    }

    public getNavColumnWidth() {
        return this.navColumnWidth;
    }

    public setNavColumnWidth(width: number) {
        this.gridDOM.style.gridTemplateColumns = `${width}px 5px 1fr`;
        this.navColumnWidth = width;
    }

    public setTOCColumnToCollapsedMode() {
        this.inCollapsibleTOCMode = true;
        this.setTOCColumnWidth(0);
        this.resizeHandleDOM.style.cursor = 'inherit';
        this.docTOCOpenPanelDOM.style.display = 'flex';
        this.docTOCClosePanelDOM.style.display = 'none';
    }

    public setTOCColumnToUncollapsedMode() {
        this.inCollapsibleTOCMode = false;
        this.setTOCColumnWidth(350);
        this.resizeHandleDOM.style.cursor = 'ew-resize';
        this.docTOCOpenPanelDOM.style.display = 'none';
        this.docTOCClosePanelDOM.style.display = 'none';

    }

    public getTOCColumnWidth() {
        return this.docTOCColumnWidth;
    }

    public setTOCColumnWidth(width: number) {
        this.docGridDOM.style.gridTemplateColumns = `1fr 5px ${width}px`;
        this.docTOCColumnWidth = width;
    }

    public openAllNavParents(el: HTMLElement): void {
        const parent = el.parentElement!.closest('.nav-tree-parent');

        // only add is-open on tree parent elements
        if (el.classList.contains('nav-tree-parent')) {
            el.classList.add('is-open');
        }

        if (parent !== null) {
            this.openAllNavParents(parent as HTMLElement);
        }
    }

    public onDocLoad(event?: Event) {

        // set up editor and schema doc sections
        const schemaDocGenDOM = document.getElementById('schema-generated-documentation');
        const editorRootDOM = document.getElementById('configuration-editor-example');

        // if content is a plugin see if the schema doc or editor elements were found
        if (this.docKind === 'pluginVersion' && (schemaDocGenDOM !== null || editorRootDOM !== null)) {
            this.schemaDocGen!.setSchema(JSON.parse((this.docResource as DocResourceKindMapping['pluginVersion']).schema));
            schemaDocGenDOM!.innerHTML = '';
            this.schemaDocGen!.generate(schemaDocGenDOM!);

            // add permalinks
            const schemaPropertyPathEls = document.querySelectorAll('.element-info-wrapper .element-path') as NodeListOf<HTMLSpanElement>;

            schemaPropertyPathEls.forEach((el: HTMLSpanElement) => {
                const permalinkDOM = document.createElement('a');
                const wrapperDOM = el.closest('.element-info-wrapper')!;

                // we don't use slugName for the url since it transforms
                // beyond url escaping and so can't be reliably decoded
                const permalinkUrl = `#${encodeURIComponent(el.innerHTML)}`;

                permalinkDOM.classList.add('header-anchor');
                permalinkDOM.href = permalinkUrl;
                permalinkDOM.innerHTML = `<i class="fa-solid fa-link"></i>`;

                if (!wrapperDOM.previousElementSibling?.classList.contains('header-anchor')) {
                    wrapperDOM.insertAdjacentElement('beforebegin', permalinkDOM);
                }
            });

            this.schemaDocGen!.addExpandCollapse('expanded');
            this.schemaDocGen!.addToolbar();
            // in case the toolbar is used, set a min-height on the schemaDocGenDOM element to its current "expanded size" (now that it has content) so it remains this "tall" even when the filter is applied and things are set to display:none (removed from layout).  Otherwise it doesn't do janky scroll things when your filter has no elements (sort of zips/collapses you to the bottom of the page because there's no more content to layout below).
            schemaDocGenDOM!.style.minHeight = `${schemaDocGenDOM!.offsetHeight}px`;

            // if we have the editor element, render an editor with our defaults and schema
            if (editorRootDOM !== null) {
                editorRootDOM.style.position = 'relative';
                editorRootDOM.style.width = '100%';
                const height = editorRootDOM.style.height;
                if (height === '' || height === null || height === undefined) {
                    editorRootDOM.style.height = '500px';
                }

                const options: GetDefaultsOptions = { useDataTypeDefaultIfNotFound: false };
                const defaults = JSON.stringify(this.schemaDocGen!.getSchemaDefault(options), undefined, 4);

                const name = (editorRootDOM.hasAttribute('data-name')) ? editorRootDOM.getAttribute('data-name') : 'Editor'; // when an attribute does not exist, technically getAttribute() can return an empty string for older DOM spec implementations (although modern browsers return null).  we use hasAttribute() here so that if it's specified at all (even specified as an empty string) we'll use it.
                const version = stringifyVersion(this.docResource as DocResourceKindMapping['pluginVersion']);
                const editorConfig = {
                    rootElement: editorRootDOM,
                    scrollContextElement: this.docContentDOM,
                    name: name as string, // this will always be a string given the hasAttribute() check, TS just doesn't know it :|
                    nameEditable: false,
                    content: defaults,
                    contentEditable: true,
                    pluginVersions: [version],
                    pluginVersionSelected: version,
                    schema: (this.docResource as DocResourceKindMapping['pluginVersion']).schema,
                    schemaUri: "https://sixclear.com/jade/docs.plugin.schema.json",
                    defaultFoldLevel: 0,
                    theme: 'vs-dark'
                };

                // @ts-expect-error we assume the bootstrapper has been loaded in a script tag
                this.editor = WebpackWrapper_JADEConfigurationEditor.init(editorConfig);
            }
        }

        // @ts-expect-error we assume the bootstrapper has been loaded in a script tag
        const editorSingleton = WebpackWrapper_JADEConfigurationEditor.getMonacoEditorSingleton();

        document.querySelectorAll('pre > code').forEach((el: Element) => {
            const code = (el as HTMLElement).textContent;

            if (code === null) {
                console.error(`This <code> element does not have any content:`)
                console.error(el);
                return;
            }

            let language: string = 'plaintext';
            const preEl = el.parentElement;
            const hasClassLanguageJADE = el.classList.contains('language-jade');
            const hasClassLanguageJSON = el.classList.contains('language-json');
            const hasClassLanguageRegex = el.classList.contains('language-regex');

            if (hasClassLanguageJADE || hasClassLanguageJSON) {
                language = 'json';
            }
            else if (hasClassLanguageRegex) {
                language = 'regexLanguage'
            }

            if (preEl === null || preEl.nodeName.toUpperCase() !== 'PRE') {
                console.error(`The following code block is not formatted as '<pre><code></code></pre>' and was not highlighted:\n${code}`);
                return;
            }

            const trimmedCode = code.replace(/^\s+|\s+$/g, '');
            const model = editorSingleton.createModel(trimmedCode, language);

            const lineHeight = 18;
            const fontSize = 13;
            const editorPadding = 10;

            // 18 is line-height of monaco line
            const contentHeight = (model.getLineCount()) * lineHeight + (editorPadding * 2);

            preEl.style.width = '100%';
            preEl.style.height = `${contentHeight}px`;
            preEl.style.background = `#1e1e1e`;
            preEl.style.padding = `0 ${editorPadding}px;`
            preEl.style.overflow = 'hidden';
            preEl.innerHTML = ``;

            editorSingleton.create(preEl, {
                model,
                language,
                lineHeight,
                fontSize,
                theme: 'vs-dark-extended',
                scrollBeyondLastLine: false,
                showFoldingControls: "never",
                minimap: { enabled: false },
                lineNumbers: 'off',
                automaticLayout: true,
                readOnly: true,
                "semanticHighlighting.enabled": true,
                renderLineHighlightOnlyWhenFocus: true,
                padding: {
                    top: editorPadding,
                    bottom: editorPadding
                },
                scrollbar: {
                    verticalScrollbarSize: 0,
                    alwaysConsumeMouseWheel: false,
                    handleMouseWheel: false
                }
            });

        });

        // highlight remaining code blocks using highlight js
        document.querySelectorAll('code').forEach((el:Element) => {
            highlighter.highlightElement(el as HTMLElement);
        });

        // scroll to element if there's a hash in the url
        if (window.location.hash) {
            let hash = window.location.hash.split('#')[1];
            let elToScrollTo = document.getElementById(hash);

            // the plugin doc generator uses unescaped id's for the cards
            // so if we can't find the el using the escaped hash we decode it
            // and try again
            if (elToScrollTo === null) {
                hash = decodeURIComponent(hash)
                elToScrollTo = document.getElementById(hash);
            }

            if (elToScrollTo !== null) {
                scrollIntoView(elToScrollTo, { time: 100, align: { top: 0, topOffset: 20 } });
            }
        }
        else {
            this.docContentDOM.scrollTop = 0;
        }

        // scroll selected nav item into view
        const navElToScrollTo = document.querySelector('.nav-tree-leaf.selected');

        if (navElToScrollTo !== null) {

            const elRect = navElToScrollTo.getBoundingClientRect();

            if (!(
                elRect.top >= 0 &&
                elRect.left >= 0 &&
                elRect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                elRect.right <= (window.innerWidth || document.documentElement.clientWidth)
            )) {
                scrollIntoView(navElToScrollTo, { time: 100, align: { top: .25, topOffset: 0 } });
            }

        }

        if (this.docKind === 'example') {
            this.addExampleDownloadSection();
        }

        if (this.editor !== undefined) {
            setTimeout(() => {
                this.editor?.render();
            }, 1000)
        }

        (document.activeElement as HTMLElement)?.blur();
    }

    protected onLogout(event: DOMEvent<MouseEvent>) {
        this.session.signOut();
    }

    public onDocSelected(event?: DOMEvent<MouseEvent>) {
        // event?.eventData.preventDefault();
        // event?.eventData.stopPropagation();

        // const target = event?.eventData.target as HTMLElement;
        // const anchorEl = target.closest('.nav-tree-leaf') as HTMLAnchorElement;
        // const url = anchorEl.href;
        // const docContentType = DOM.getAttribute('data-content-type', anchorEl) as ResourceKindWithDocsJSON;

        // if (url) {
        //     history.pushState({ url, docContentType }, '', url);
        //     this.navigateToDocument(url, docContentType);

        //     if (this.inCollapsibleNavMode) {
        //         this.onNavCloseSelected()
        //     }
        // }
    }

    public onNavTreeParentSelected(event: DOMEvent<MouseEvent>) {
        const target = event.eventData.target as HTMLElement;
        const parentAnchor = target.closest('.nav-tree-leaf');
        const parentLi = target.closest('.nav-tree-parent');

        // need to check to make sure that target isn't a leaf anchor since we're nested
        if (!parentAnchor && parentLi) {
            const isOpen = parentLi.classList.contains('is-open');

            if (isOpen) {
                parentLi.classList.remove('is-open');
            }
            else {
                parentLi.classList.add('is-open');
            }
        }

    }

    public onPopState(event: PopStateEvent) {
        this.navigateToDocument(event.state.url, event.state.docContentType);
    }

    public onWindowResize() {
        const currentWidth = window.innerWidth;

        this.navColumnWidth = this.navContentDOM.getBoundingClientRect().width;
        this.docTOCColumnWidth = this.docTOCDOM.getBoundingClientRect().width;

        if (currentWidth < 800) {
            this.setViewerToCollapsibleNavMode();

            if (currentWidth < 600) {
                this.setTOCColumnToCollapsedMode();
            }
            else {
                this.setTOCColumnToUncollapsedMode();
            }
        }
        else {
            this.setViewerToUncollapsedNavMode();
        }
    }

    public onNavResizeMouseUp(event: MouseEvent) {
        this.gridDOM.style.cursor = 'default';
        window.removeEventListener("mousemove", this.onNavResizeMouseMove);
        window.removeEventListener("mouseup", this.onNavResizeMouseUp);
    }

    public onNavResizeMouseMove(event: MouseEvent) {

        window.getSelection()?.removeAllRanges();
        let navColumnWidth = this.navColumnWidthOnMouseDown + (event.clientX - this.clientXOnMouseDown);

        const navColumnWidthMin = 200;
        const navColumnWidthMax = document.body.offsetWidth - this.docTOCColumnWidth - this.minContentWidth;

        if (navColumnWidth < navColumnWidthMin) {
            navColumnWidth = navColumnWidthMin;
        }
        else if (navColumnWidth > navColumnWidthMax) {
            navColumnWidth = navColumnWidthMax;
        }
        this.setNavColumnWidth(navColumnWidth);

        // if we have an editor, re-render it so that it resizes according to the new container width etc.
        if (this.editor !== undefined) {
            this.editor.render();
        }

    }

    public onNavResizeMouseDown(event: DOMEvent<MouseEvent>) {
        if (!this.inCollapsibleNavMode) {
            this.gridDOM.style.cursor = 'ew-resize';
            this.clientXOnMouseDown = event.eventData.clientX;
            this.navColumnWidthOnMouseDown = this.getNavColumnWidth();
            this.onNavResizeMouseMove = this.onNavResizeMouseMove.bind(this);
            this.onNavResizeMouseUp = this.onNavResizeMouseUp.bind(this);
            window.addEventListener("mousemove", this.onNavResizeMouseMove);
            window.addEventListener("mouseup", this.onNavResizeMouseUp);
        }
    }

    public onTOCResizeMouseUp(event: MouseEvent) {
        this.gridDOM.style.cursor = 'default';
        window.removeEventListener("mousemove", this.onTOCResizeMouseMove);
        window.removeEventListener("mouseup", this.onTOCResizeMouseUp);
    }

    public onTOCResizeMouseMove(event: MouseEvent) {

        window.getSelection()?.removeAllRanges();

        let tocColumnWidth = this.docTOCColumnWidthOnMouseDown - (event.clientX - this.clientXOnMouseDown);

        const tocColumnWidthMin = 200;
        const tocColumnWidthMax = document.body.offsetWidth - this.navColumnWidth - this.minContentWidth;

        if (tocColumnWidth < tocColumnWidthMin) {
            tocColumnWidth = tocColumnWidthMin;
        }
        else if (tocColumnWidth > tocColumnWidthMax) {
            tocColumnWidth = tocColumnWidthMax;
        }

        this.setTOCColumnWidth(tocColumnWidth);

        // if we have an editor, re-render it so that it resizes according to the new container width etc.
        if (this.editor !== undefined) {
            this.editor.render();
        }

    }

    public onTOCResizeMouseDown(event: DOMEvent<MouseEvent>) {
        if (!this.inCollapsibleTOCMode) {
            this.gridDOM.style.cursor = 'ew-resize';
            this.clientXOnMouseDown = event.eventData.clientX;
            this.docTOCColumnWidthOnMouseDown = this.getTOCColumnWidth();
            this.onTOCResizeMouseMove = this.onTOCResizeMouseMove.bind(this);
            this.onTOCResizeMouseUp = this.onTOCResizeMouseUp.bind(this);
            window.addEventListener("mousemove", this.onTOCResizeMouseMove);
            window.addEventListener("mouseup", this.onTOCResizeMouseUp);
        }
    }

    public onNavOpenSelected(event?: DOMEvent<MouseEvent>) {
        const navWidth = {
            width: this.getNavColumnWidth()
        }

        this.navOpenButtonDOM.style.display = 'none';
        this.navCloseButtonDOM.style.display = 'flex';
        this.docTOCOpenPanelDOM.style.display = 'none';

        anime({
            targets: navWidth,
            width: window.innerWidth - 10,
            duration: 250,
            easing: 'easeOutQuint',
            update: () => {
                this.setNavColumnWidth(navWidth.width)
            }
        });
    }

    public onNavCloseSelected(event?: DOMEvent<MouseEvent>) {
        const navWidth = {
            width: this.getNavColumnWidth()
        }

        this.navOpenButtonDOM.style.display = 'flex';
        this.navCloseButtonDOM.style.display = 'none';
        this.docTOCOpenPanelDOM.style.display = 'flex';
        anime({
            targets: navWidth,
            width: 0,
            duration: 250,
            easing: 'easeOutQuint',
            update: () => {
                this.setNavColumnWidth(navWidth.width);
            }
        });
    }

    public onTOCOpenSelected(event: DOMEvent<MouseEvent>) {
        const tocWidth = {
            width: 0
        }

        this.docContentDOM.style.display = 'none';
        this.docTOCOpenPanelDOM.style.display = 'none';
        this.docTOCClosePanelDOM.style.display = 'flex';

        const finalWidth = window.innerWidth - this.getNavColumnWidth() - 20;

        console.log(finalWidth);

        anime({
            targets: tocWidth,
            width: finalWidth,
            duration: 250,
            easing: 'easeOutQuint',
            update: () => {
                this.setTOCColumnWidth(tocWidth.width);
            }
        });
    }

    public onTOCCloseSelected(event?: DOMEvent<MouseEvent>) {
        const tocWidth = {
            width: this.getTOCColumnWidth()
        }

        this.docContentDOM.style.display = 'block';
        this.docTOCOpenPanelDOM.style.display = 'flex';
        this.docTOCClosePanelDOM.style.display = 'none';

        anime({
            targets: tocWidth,
            width: 0,
            duration: 250,
            easing: 'easeOutQuint',
            update: () => {
                this.setTOCColumnWidth(tocWidth.width);
            }
        });
    }

    public onTOCLinkSelected(event: DOMEvent<MouseEvent>) {
        event.eventData.preventDefault();
        event.eventData.stopPropagation();

        const tocElement = (event.eventData.target as HTMLElement).closest('a')
        const href = tocElement?.href!;
        const elToScrollTo = document.getElementById(href.split('#')[1]);

        // the TOC right side panel collapse behavior is controlled by CSS
        // so use the display state of the open toc tab to know if we need to
        // close the panel
        if (this.docTOCOpenPanelDOM.style.display !== '') {
            this.onTOCCloseSelected();
        }

        history.pushState({ url: href, docContentType: this.docKind }, '', href);
        scrollIntoView(elToScrollTo, { time: 250, align: { top: 0, topOffset: 20 } });
    }

    public onPermalinkSelected(event: DOMEvent<MouseEvent>) {
        event.eventData.preventDefault();
        event.eventData.stopPropagation();

        const permalinkEl = (event.eventData.target as HTMLElement).closest('a')
        const href = permalinkEl?.href!;

        history.pushState({ url: href, docContentType: this.docKind }, '', href);
        scrollIntoView(permalinkEl, { time: 250, align: { top: 0, topOffset: 20 } });
    }

    public async fetchContentHTML(url: string): Promise<string> {
        const contentHTMLUrl = `${url}/content.html`;

        const response = await fetch(contentHTMLUrl, {
            method: 'GET',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'text/plain'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: undefined // this must be undefined for a GET request else an error is returned
        });

        return response.text();
    }

    // public async fetchContentJSON(url:string) : Promise<DocContentPath> {
    //     const contentJSONUrl = `${url}/content.json`;

    //     const response = await fetch(contentJSONUrl, {
    //       method: 'GET',
    //       mode: 'same-origin',
    //       cache: 'no-cache',
    //       credentials: 'same-origin',
    //       headers: {
    //         'Content-Type': 'application/json'
    //       },
    //       redirect: 'follow',
    //       referrerPolicy: 'no-referrer',
    //       body: undefined // this must be undefined for a GET request else an error is returned
    //     });

    //     return response.json();
    // }

    /**
     * Sends a message to LV to update the window title
     * @param title the new window title
     */
    public updateWindowTitle(title: string) {
        if (this.deploymentEnvironment === 'offline') {
            try {
                if ((window as any)?.chrome?.webview !== undefined) {
                    // @ts-ignore
                    window.chrome.webview.postMessage({
                        status: 'OK',
                        operation: 'updateWindowTitle',
                        data: {
                            nodeId: title
                        }
                    });
                }
            }
            catch (error) {
                console.error(error);
            }
        }
        else {
            document.title = title;
        }

    }

    /**
     * Adds a nofitication to the editor
     * @param message the notification message
     * @param type the notification type
     */
    public addEditorNotification(message: string, type: JADEConfigurationEditorNotificationType) {

        // if this is ever invoked by the LV side (using the WebView2 call async script method) it can only be as a result of an editor button click essentially, but we check the editor's existence anyway, just in case
        if (this.editor !== undefined) {
            this.editor.addNotification(message, type);
        }

    }

    protected onAuthStateChanged(event: ComponentClassEventPayload<SessionStandalone, SessionStandaloneEventMap, 'authStateChanged'>) {
        if (event.eventData === null) {
            this.renderUserLoggedOut();
        }
        else {
            this.renderUserLoggedIn();
        }
    }

    protected addExampleDownloadSection() {
        const exampleDownload = DOM.createHTMLElement('div');

        exampleDownload.classList.add('example-download');
        exampleDownload.innerHTML = this.renderExampleDownloadSection();

        DOM.prepend(exampleDownload, this.docTOCDOM);
    }

    protected refreshExampleDownloadSection() {
        const exampleDownload = document.getElementsByClassName('example-download')[0];

        if (exampleDownload !== null) {
            exampleDownload.innerHTML = this.renderExampleDownloadSection();
        }
    }

    protected renderExampleDownloadSection() {
        return `
            <div id="download-example">
                ${this.isDownloadingExample ? 'Downloading' : 'Download'} Example <div class="icon">JADA</div>
            </div>
        `
    }

    protected renderUserLoggedIn(showDropdown: boolean = false) {
        this.userControlsDOM.innerHTML = this.renderUserLoggedInHeaderSection(showDropdown);
    }

    protected renderUserDropdown(show: boolean = false) {

        const userNav = this.session.getUserNavigation()
            .map((navItem) => {
                return `<a class="dropdown-item" href="${navItem.link}">${navItem.desktopMenuTitle}</a>`
            })
            .join('');

        return `
            <div id="account-menu-dropdown" class="dropdown-menu" style="z-index: 9999; ${show === false ? 'display: none;' : 'display: block;'}">
                <div id="arrow" data-popper-arrow></div>
                <h6 id="logged-in-menu-display-name" class="dropdown-header">${this.session.getLoggedInUser()!.displayName}</h6>
                ${userNav}
                <div class="dropdown-divider"></div>
                <a class="dropdown-item" id="desktop-logout" href="#">Logout&nbsp;&nbsp;<i class="fas fa-sign-out-alt"></i></a>
            </div>
        `
    }

    protected renderUserLoggedInHeaderSection(showDropdown: boolean = false) {

        const initials = this.session.getLoggedInUser()!
            .displayName!
            .split(' ')
            .map((name) => name.substring(0, 1).toUpperCase())
            .slice(0, 2)
            .join('');

        return `
            <div id="logged-in-btn">
                <a id="logged-in-icon-btn">
                    <div class="circle">
                        <p class="circle-inner">${initials}</p>
                    </div>
                    <i class="fas fa-angle-down"></i>
                </a>
            </div>
            ${this.renderUserDropdown(showDropdown)}
        `
    }

    protected renderUserLoggedOutInHeaderSection() {
        return `
            <div id="login-buttons">
                <a href="/views/auth/signin">Login</a>
                /
                <a href="/views/auth/signup">Signup</a>
            </div>
        `
    }

    protected renderUserLoggedOut() {
        this.userControlsDOM.innerHTML = this.renderUserLoggedOutInHeaderSection();
    }

    protected closeLoggedInMenu() {
        this.popoverOverlay.removeEventListener('click', this.closeLoggedInMenu);

        this.desktopLoggedInMenuPopper?.destroy();
        this.desktopLoggedInMenuPopper = undefined;
        this.popoverOverlay.style.display = 'none';

        this.renderUserLoggedIn(false);
    }

    protected closeJadeVersionSelect() {
        this.popoverOverlay.removeEventListener('click', this.closeJadeVersionSelect);

        this.jadeSelectPopper?.destroy();
        this.jadeSelectPopper = undefined;

        this.popoverOverlay.style.display = 'none';

        const jadeSelectDropdown = document.querySelector(".jade-version-select-dropdown") as HTMLElement;

        jadeSelectDropdown.style.display = 'none';
    }

    protected onUserMenuButtonClicked() {
        if (this.desktopLoggedInMenuPopper === undefined) {

            this.popoverOverlay.addEventListener('click', this.closeLoggedInMenu);

            const menuBtn = document.getElementById("logged-in-btn") as HTMLElement;
            const menu = document.getElementById("account-menu-dropdown") as HTMLElement;

            menu.style.display = 'block';
            this.popoverOverlay.style.display = 'block';
            this.desktopLoggedInMenuPopper = createPopper(menuBtn, menu, {
                placement: 'bottom-end',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [5, 10],
                        },
                    },
                ],
            });
        }
        else {
            this.closeLoggedInMenu();
        }
    }

    protected onJadeVersionSelectClicked() {
        if (this.jadeSelectPopper === undefined) {

            this.popoverOverlay.addEventListener('click', this.closeJadeVersionSelect);

            const jadeSelect = document.querySelector(".jade-version-select .version-select") as HTMLElement;
            const jadeSelectDropdown = document.querySelector(".jade-version-select-dropdown") as HTMLElement;

            jadeSelectDropdown.style.display = 'flex';
            // jadeSelectDropdown.style.width = `${jadeSelect.getBoundingClientRect().width + 20}px`;
            this.popoverOverlay.style.display = 'block';
            this.jadeSelectPopper = createPopper(jadeSelect, jadeSelectDropdown, {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [-10, 15],
                        },
                    },
                ],
            });
        }
        else {
            this.closeJadeVersionSelect();
        }
    }

    protected async onDownloadExampleSelected() {

        this.isDownloadingExample = true;
        this.refreshExampleDownloadSection();

        const downloadResponse = await fetch(`${this.jadeExampleDownloadUrl}?id=${this.docResource!.id}`);
        const downloadBlob = await downloadResponse.blob();

        const blobReader = new zip.BlobReader(downloadBlob);
        const zipReader = new zip.ZipReader(blobReader);

        const entries = await zipReader.getEntries();

        if (entries.length > 1) {
            const url = window.URL.createObjectURL(downloadBlob);
            const anchorEl = document.createElement('a');

            const build = (this.docResource as DocResourceKindMapping['example']).builds[0];
            const downloadFilepathParts = build.downloadGcpBucketFilepath.split('/');
            const downloadName = downloadFilepathParts[downloadFilepathParts.length - 1];

            anchorEl.href = url;
            anchorEl.download = downloadName;
            document.body.appendChild(anchorEl);
            anchorEl.click();
            anchorEl.remove();
        }
        else {
            const jadaWriter = new zip.BlobWriter();
            const jadaBlob = await entries[0].getData!(jadaWriter);

            const url = window.URL.createObjectURL(jadaBlob);
            const anchorEl = document.createElement('a');

            anchorEl.href = url;
            anchorEl.download = entries[0].filename;
            document.body.appendChild(anchorEl);
            anchorEl.click();
            anchorEl.remove();
        }

        this.isDownloadingExample = false;
        this.refreshExampleDownloadSection();
    }

}